import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { useFieldArray, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import zxcvbn from "zxcvbn";
import { Button } from "aible";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/aible/Dialog/Dialog";
import * as adminReducer from "containers/Admin/reducer";
import * as loginReducer from "containers/Login/reducer";
import { useDispatch, useSelector } from "types/store";
import styles from "./style.module.css";
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PASSWORD_SCORE = 4;
const emptyUser = {
    email: "",
    fullname: "",
    password: ""
};
function validatePasswordStrength(password) {
    const result = zxcvbn(password);
    if (result.score >= MIN_PASSWORD_SCORE) {
        return true;
    }
    const warning = result.feedback.warning === "This is a top-10 common password"
        ? "This password is too common."
        : result.feedback.warning;
    const feedback = [warning, ...result.feedback.suggestions].filter(Boolean);
    return feedback.length > 0 ? feedback.join(" ") : "Use a longer, less predictable password.";
}
async function getRegisterUsersBulkErrorMessage(error) {
    const fallback = "Users could not be created.";
    const response = error?.response;
    if (response) {
        try {
            const data = await response;
            if (Array.isArray(data)) {
                return (data
                    .map((item) => item?.message)
                    .filter(Boolean)
                    .join(" ") || fallback);
            }
            if (typeof data === "string") {
                return data;
            }
            if (data && typeof data === "object" && "message" in data) {
                return String(data.message);
            }
        }
        catch {
            return fallback;
        }
    }
    return error instanceof Error ? error.message : fallback;
}
const CreateUsersModal = ({ showModal, updateShowModal }) => {
    const dispatch = useDispatch();
    const activeTenant = useSelector((state) => loginReducer.getSelectedTenant(state));
    const tenantToken = useSelector((state) => loginReducer.getAuthToken(state));
    // const activeTenantInviteToken = useSelector((state) =>
    //   activeTenant?.id ? loginReducer.getTenantInviteToken(activeTenant.id, state) : undefined
    // );
    const [error, updateError] = useState(undefined);
    const [isLoading, updateIsLoading] = useState(false);
    const { control, formState: { errors }, handleSubmit, register, reset } = useForm({
        defaultValues: {
            users: [emptyUser]
        }
    });
    const { append, fields, remove } = useFieldArray({
        control,
        name: "users"
    });
    const resetFields = () => {
        reset({ users: [emptyUser] });
        updateError(undefined);
    };
    const closeModal = () => {
        updateShowModal(false);
        resetFields();
    };
    const handleCreateUsers = async (values) => {
        updateIsLoading(true);
        updateError(undefined);
        try {
            const users = values.users.map((user) => ({
                email: user.email.trim(),
                fullname: user.fullname.trim(),
                password: user.password
            }));
            await dispatch(loginReducer.handleRegisterBulk(users, tenantToken));
            dispatch(adminReducer.updateUsersCall(activeTenant));
            toast("Users have been created.", {
                type: toast.TYPE.SUCCESS,
                position: toast.POSITION.TOP_CENTER,
                autoClose: 7500
            });
            closeModal();
        }
        catch (e) {
            updateError(await getRegisterUsersBulkErrorMessage(e));
        }
        finally {
            updateIsLoading(false);
        }
    };
    return (React.createElement(Dialog, { open: showModal, onOpenChange: () => closeModal() },
        React.createElement(DialogContent, null,
            React.createElement(DialogHeader, null,
                React.createElement(DialogTitle, null, "Create users")),
            React.createElement("form", { autoComplete: "off", onSubmit: handleSubmit(handleCreateUsers) },
                React.createElement("div", { className: styles.createUsersModalBody },
                    fields.map((field, index) => {
                        const rowErrors = errors.users?.[index];
                        return (React.createElement("div", { className: `${styles.createUsersRow} ${fields.length > 1 ? styles.createUsersRowWithRemove : ""}`, key: field.id },
                            React.createElement("div", { className: styles.createUsersField },
                                React.createElement("label", { className: styles.createUsersLabel },
                                    "Email",
                                    React.createElement("span", { className: styles.createUsersRequired }, "*")),
                                React.createElement("input", { autoComplete: "off", className: `${styles.createUsersInput} ${rowErrors?.email ? styles.createUsersInputError : ""}`, placeholder: "Email", ...register(`users.${index}.email`, {
                                        pattern: {
                                            message: "Enter a valid email address.",
                                            value: emailRegex
                                        },
                                        required: "Email is required."
                                    }) }),
                                rowErrors?.email && React.createElement("div", { className: styles.createUsersFieldError }, rowErrors.email.message)),
                            React.createElement("div", { className: styles.createUsersField },
                                React.createElement("label", { className: styles.createUsersLabel },
                                    "Full name",
                                    React.createElement("span", { className: styles.createUsersRequired }, "*")),
                                React.createElement("input", { autoComplete: "off", className: `${styles.createUsersInput} ${rowErrors?.fullname ? styles.createUsersInputError : ""}`, placeholder: "Full name", ...register(`users.${index}.fullname`, { required: "Full name is required." }) }),
                                rowErrors?.fullname && (React.createElement("div", { className: styles.createUsersFieldError }, rowErrors.fullname.message))),
                            React.createElement("div", { className: styles.createUsersField },
                                React.createElement("label", { className: styles.createUsersLabel },
                                    "Password",
                                    React.createElement("span", { className: styles.createUsersRequired }, "*")),
                                React.createElement("input", { autoComplete: "new-password", className: `${styles.createUsersInput} ${rowErrors?.password ? styles.createUsersInputError : ""}`, placeholder: "Password", type: "password", ...register(`users.${index}.password`, {
                                        required: "Password is required.",
                                        validate: validatePasswordStrength
                                    }) }),
                                rowErrors?.password && (React.createElement("div", { className: styles.createUsersFieldError }, rowErrors.password.message))),
                            fields.length > 1 && (React.createElement("div", { className: styles.createUsersRemoveField },
                                React.createElement(Button, { "aria-label": "Remove user", className: styles.createUsersRemoveButton, isSmall: true, onClick: (event) => {
                                        event.preventDefault();
                                        remove(index);
                                    }, title: "Remove user" },
                                    React.createElement(FontAwesomeIcon, { icon: faXmark }))))));
                    }),
                    React.createElement(Button, { onClick: (event) => {
                            event.preventDefault();
                            append(emptyUser);
                        } }, "Add row"),
                    React.createElement("div", { className: styles.addUserErrorContainer }, Boolean(error) && React.createElement("div", { className: styles.addUserErrorText }, error))),
                React.createElement(DialogFooter, null,
                    React.createElement(Button, { onClick: (event) => {
                            event.preventDefault();
                            closeModal();
                        } }, "Close"),
                    React.createElement(Button, { type: "primary", isLoading: isLoading }, "Create users"))))));
};
export default CreateUsersModal;
